import React, { useEffect, useState } from "react";
import "./Header.scss";
import { motion } from "framer-motion";
import { AppWrap } from "../../wrapper";
import { images } from "../../constants";

const Header = () => {
  const [helloCount, setHelloCount] = useState(0);
  const [robinCount, setRobinCount] = useState(0);
  const [jobCounts, setJobCounts] = useState([0, 0]);
  const [currentLine, setCurrentLine] = useState(1);
  const [cursorVisible, setCursorVisible] = useState(true);

  const fullHello = "Hi, I am";
  const fullRobin = "Robin Winters...";
  const jobLines = [
    "The United Kingdom based,",
    "Software & DevOps Engineer...",
  ];

  useEffect(() => {
    let timers = [];
    let totalDelay = 0;

    const randomSpeed = (min = 50, max = 120) =>
      Math.floor(Math.random() * (max - min + 1)) + min;

    fullHello.split("").forEach((_, idx) => {
      const delay = randomSpeed();
      totalDelay += delay;
      timers.push(setTimeout(() => setHelloCount(idx + 1), totalDelay));
    });

    totalDelay += 300;
    timers.push(setTimeout(() => setCurrentLine(2), totalDelay));

    fullRobin.split("").forEach((_, idx) => {
      const delay = randomSpeed();
      totalDelay += delay;
      timers.push(setTimeout(() => setRobinCount(idx + 1), totalDelay));
    });

    totalDelay += 400;
    timers.push(setTimeout(() => setCurrentLine(3), totalDelay));

    jobLines.forEach((line, lineIndex) => {
      line.split("").forEach((_, idx) => {
        const delay = randomSpeed();
        totalDelay += delay;
        timers.push(
          setTimeout(() => {
            setJobCounts((prev) => {
              const newCounts = [...prev];
              newCounts[lineIndex] = idx + 1;
              return newCounts;
            });
          }, totalDelay)
        );
      });
      totalDelay += 300;
    });

    timers.push(
      setTimeout(() => {
        setTimeout(() => {
          setCursorVisible(false);
        }, 2000);
      }, totalDelay + 1500)
    );

    return () => timers.forEach(clearTimeout);
  }, []);

  const renderTyping = (
    text,
    count,
    line,
    isJob = false,
    jobLineIndex = 0
  ) => {
    const isCurrentJobLine =
      currentLine === 3 &&
      jobLineIndex === jobCounts.findIndex((c, i) => c < jobLines[i].length);

    return (
      <>
        {text.split("").map((char, idx) => (
          <span
            key={idx}
            className={`typing-letter ${idx < count ? "visible" : "invisible"}`}
          >
            {char === " " ? "\u00A0" : char}
            {idx === count - 1 &&
              ((currentLine === line && !isJob) || (isJob && isCurrentJobLine)) &&
              cursorVisible && <span className="real-cursor" />}
          </span>
        ))}
      </>
    );
  };

  return (
    <div className="app__header app__flex">
      {/* LEFT SIDE */}
      <motion.div
        whileInView={{ x: [-100, 0], opacity: [0, 1] }}
        transition={{ duration: 0.5 }}
        className="app__header-info"
      >
        <div className="app__header-badge-container">
          <div className="app__header-badge">
            <div className="badge-cmp app__flex typing-container">
              {/* Waving */}
              <div className="typing-wrapper">
                <p className="p-text typing-real typing-placeholder">👋 {fullHello}</p>
                <p className="p-text typing-real typing-animated">
                  <motion.span
                    className="waving-emoji"
                    animate={{ rotate: [0, 30, -20, 10, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                    style={{
                      display: "inline-block",
                      marginRight: "0.5rem",
                      originX: 0.7,
                      originY: 0.7,
                    }}
                  >
                    👋
                  </motion.span>
                  {renderTyping(fullHello, helloCount, 1)}
                </p>
              </div>

              {/* Robin */}
              <div className="typing-wrapper">
                <h1 className="head-text typing-real typing-placeholder">{fullRobin}</h1>
                <h1 className="head-text typing-real typing-animated">
                  {renderTyping(fullRobin, robinCount, 2)}
                </h1>
              </div>

              {/* Jobs */}
              <div className="typing-wrapper job-lines-wrapper">
                <div className="typing-real job-lines-text typing-placeholder">
                  {jobLines.map((line, index) => (
                    <div key={index} className="job-line">{line}</div>
                  ))}
                </div>
                <div className="typing-real job-lines-text typing-animated">
                  <div className="job-lines-inner">
                    {jobLines.map((line, index) => (
                      <div
                        key={index}
                        className={`job-line ${index === 1 ? "job-line-important" : ""}`}
                      >
                        {renderTyping(line, jobCounts[index], 3, true, index)}
                      </div>
                    ))}
                    {jobCounts.every((count, i) => count === jobLines[i].length) && (
                      <span className="underline" />
                    )}
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </motion.div>

      {/* RIGHT SIDE */}
      <motion.div
        whileInView={{ opacity: [0, 1] }}
        transition={{ duration: 0.5, delayChildren: 0.5 }}
        className="app__header-img"
      >
        <motion.img
          whileInView={{ scale: [0, 1] }}
          transition={{ duration: 1, ease: "easeInOut" }}
          src={images.circle}
          alt="profile_circle"
          className="overlay_circle"
        />
        <img src={images.profile} alt="profile" />
      </motion.div>

      {/* CIRCLES */}
      <motion.div className="app__header-circles">
        {[images.python, images.javascript, images.typescript, images.ruby].map(
          (circle, index) => (
            <motion.div
              key={`circle-${index}`}
              className="circle-cmp app__flex"
              initial={{ scale: 1 }}
              animate={{
                scale: [1, 1.1, 1],
                filter: [
                  "drop-shadow(0 0 0px rgba(0,0,0,0.5))",
                  "drop-shadow(0 0 10px rgba(0,0,0,0.5))",
                  "drop-shadow(0 0 0px rgba(0,0,0,0.5))",
                ],
              }}
              transition={{
                duration: 6,
                repeat: Infinity,
                repeatType: "loop",
                ease: "easeInOut",
                times: [0, 0.5, 1],
              }}
            >
              <img src={circle} alt="languages" />
            </motion.div>
          )
        )}
      </motion.div>
    </div>
  );
};

export default AppWrap(Header, "home");
